import { createContext, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";

/**
 * Branding provider — fetches the platform's brand config from `GET /api/branding` (public,
 * no auth) on first mount and injects colour CSS variables into `:root` so hard-coded
 * Tailwind classes like `text-gold` and `bg-bg` pick up the admin-configured values.
 *
 * Ships defaults inline so a fresh page load renders correctly even before the API call
 * completes (avoids a "white flash" while branding loads).
 */

const DEFAULTS = {
  product_name: "The Money Gurukul",
  tagline: "Financial Independence Made Simple",
  support_email: "parth@snehworld.com",
  support_whatsapp: "",
  footer_text: "© The Money Gurukul. All rights reserved.",
  social_links: { instagram: "", linkedin: "", youtube: "" },
  colors: {
    gold: "#D4AF37",
    gold_deep: "#B8912E",
    bg: "#0A0908",
    accent: "#F5D57A",
  },
  email_from_name: "The Money Gurukul",
  otp_subject: "Your login code",
  otp_header: "Your login code",
};

const BrandingContext = createContext({ branding: DEFAULTS, refresh: () => {} });

function applyCssVars(colors) {
  if (!colors) return;
  const root = document.documentElement;
  // Only apply if the admin actually overrode the shipped default — some designs deliberately
  // rely on Tailwind's default palette for surfaces (bg-card, bg-elevated), so we don't touch
  // those. This keeps the admin's blast-radius small.
  if (colors.gold) root.style.setProperty("--gold", colors.gold);
  if (colors.gold_deep) root.style.setProperty("--gold-deep", colors.gold_deep);
  // We deliberately don't override --bg / --card / --elevated automatically — those need
  // careful colour-theory work (contrast, opacity blends) that a single picker can't cover.
  // The accent variable is exposed for CTA buttons that want the alt-gold shade.
  if (colors.accent) root.style.setProperty("--gold-accent", colors.accent);
}

export function BrandingProvider({ children }) {
  const [branding, setBranding] = useState(DEFAULTS);

  const refresh = useMemo(() => async () => {
    try {
      const backend = process.env.REACT_APP_BACKEND_URL;
      const { data } = await axios.get(`${backend}/api/branding`);
      setBranding(data);
      applyCssVars(data.colors);
      // Update <title> and favicon if changed
      if (data.product_name) document.title = data.product_name;
    } catch {
      // Silent fallback — DEFAULTS keep the app usable if the API is down.
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Memoise the context value so consumers only re-render when `branding` (or the stable
  // `refresh` callback) actually changes, not on every parent render. The previous inline
  // `{ branding, refresh }` created a new object identity per render.
  const value = useMemo(() => ({ branding, refresh }), [branding, refresh]);

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  return useContext(BrandingContext);
}
